import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CerebroTextButton } from '../../shared/components';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { LoanApplicationTask, LoanApplicationTaskDocument } from '../../shared/models';

export const DeleteDocumentFileDialog: React.FC<DeleteDocumentFileDialogProps> = ({
	open = false,
	onClose,
	file,
	document: requiredDocument,
}) => {
	const api = useApi();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const mutation = useMutation(
		async ({ parentDocument, fileToDelete }: MutationVariables) => {
			await api.delete<LoanApplicationTask>(
				CerebroApiEndpoints.RequiredDocuments.SpecificFile(guid, parentDocument.guid, fileToDelete.guid)
			);
		},
		{
			onSuccess: (_data, { fileToDelete }) => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar(`'${fileToDelete.documentTitle}' deleted.`, {
					variant: 'success',
				});
			},
			onError: (error: Error) => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid));
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: onClose,
		}
	);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete &lsquo;{file.documentTitle}&rsquo;?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={onClose}>No</CerebroTextButton>
				<CerebroTextButton
					onClick={() => mutation.mutate({ parentDocument: requiredDocument, fileToDelete: file })}
				>
					Yes
				</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

interface DeleteDocumentFileDialogProps {
	open?: boolean;
	onClose: () => void;

	document: LoanApplicationTask;
	file: LoanApplicationTaskDocument;
}

interface MutationVariables {
	parentDocument: LoanApplicationTask;
	fileToDelete: LoanApplicationTaskDocument;
}
